import type { ViewMode } from "../../components/molecules/ViewModeToggle/ViewModeToggle.types";

/** KG secondary sidebar section ids — must stay in sync with sidebar nav. */
export type KgSidebarSection =
  | "canvas"
  | "dataModel"
  | "dataSources"
  | "tables"
  | "ontologies"
  | "metrics"
  | "businessRules"
  | "tribalKnowledge"
  | "reports"
  | "dataStories"
  | "platformHistory"
  | "feedback"
  | "artifact";

export type KgHeaderTitleResolver = (ctx: { viewMode?: ViewMode }) => string;

export type KgHeaderChromeMode = "search" | "actions";

export type KgHeaderActionVariant = "outline-primary" | "primary-dark";

export type KgHeaderActionId = "saveDraft" | "saveVerify";

export type KgHeaderActionConfig = {
  id: KgHeaderActionId;
  label: string;
  variant: KgHeaderActionVariant;
};

export type KgSectionHeaderChrome = {
  mode?: KgHeaderChromeMode;
  searchPlaceholder?: string;
  showViewModeToggle?: boolean;
  actions?: readonly KgHeaderActionConfig[];
              };

export const KG_ADD_DATA_MODEL_HEADER_ACTIONS: readonly KgHeaderActionConfig[] =
  [                                                                                                                                                                                                                                                                                                           
    { id: "saveDraft", label: "Save as draft", variant: "outline-primary" },
    { id: "saveVerify", label: "Save and Verify", variant: "primary-dark" },
  ] as const;

export type KgSectionConfig = {
  id: KgSidebarSection;
  pathSegment: string;
  sidebarLabel: string;
  headerTitle: string | KgHeaderTitleResolver;
  showInSidebar: boolean;
  headerChrome?: KgSectionHeaderChrome;
};

const canvasHeaderTitle: KgHeaderTitleResolver = ({ viewMode }) =>
  viewMode === "list" ? "Knowledge catalog" : "Knowledge canvas";

/** Sidebar order + route metadata — single source of truth for KG sections. */
export const KG_SECTIONS: readonly KgSectionConfig[] = [
  {
    id: "canvas",
    pathSegment: "canvas",
    sidebarLabel: "Canvas",
    headerTitle: canvasHeaderTitle,
    showInSidebar: true,
    headerChrome: {
      searchPlaceholder: "Search nodes",
      showViewModeToggle: true,
    },
  },
  {
    id: "dataSources",
    pathSegment: "data-sources",
    sidebarLabel: "Data sources",
    headerTitle: "Data sources",
    showInSidebar: true,
  },
  {
    id: "tables",
    pathSegment: "tables",
    sidebarLabel: "Tables",
    headerTitle: "Tables",
    showInSidebar: true,
  },
  {
    id: "dataModel",
    pathSegment: "data-models",
    sidebarLabel: "Data models",
    headerTitle: "Data models",
    showInSidebar: true,
    headerChrome: {
      mode: "search",
      searchPlaceholder: "Search data models",
    },
  },
  {
    id: "ontologies",
    pathSegment: "ontologies",
    sidebarLabel: "Ontologies",
    headerTitle: "Ontologies",
    showInSidebar: true,
  },
  {
    id: "metrics",
    pathSegment: "metrics",
    sidebarLabel: "Metrics",
    headerTitle: "Metrics",
    showInSidebar: true,
  },
  {
    id: "businessRules",
    pathSegment: "business-rules",
    sidebarLabel: "Business rules",
    headerTitle: "Business rules",
    showInSidebar: true,
  },
  {
    id: "tribalKnowledge",
    pathSegment: "tribal-knowledge",
    sidebarLabel: "Tribal knowledge",
    headerTitle: "Tribal knowledge",
    showInSidebar: true,
  },
  {
    id: "reports",
    pathSegment: "reports",
    sidebarLabel: "Reports",
    headerTitle: "Reports",
    showInSidebar: true,
  },
  {
    id: "dataStories",
    pathSegment: "data-stories",
    sidebarLabel: "Data stories",
    headerTitle: "Data stories",
    showInSidebar: true,
  },
  {
    id: "platformHistory",
    pathSegment: "platform-history",
    sidebarLabel: "Platform history",
    headerTitle: "Platform history",
    showInSidebar: true,
  },
  {
    id: "feedback",
    pathSegment: "feedback",
    sidebarLabel: "Feedback",
    headerTitle: "Feedback",
    showInSidebar: true,
  },
  {
    id: "artifact",
    pathSegment: "artifacts",
    sidebarLabel: "Artifacts",
    headerTitle: "Artifacts",
    showInSidebar: false,
  },
] as const;

/** Add data model builder — `/knowledge-graph/data-models/new` (not in sidebar). */
export const KG_ADD_DATA_MODEL_SECTION: KgSectionConfig = {
  id: "dataModel",
  pathSegment: "data-models",
  sidebarLabel: "Data models",
  headerTitle: "Add data model",
  showInSidebar: false,
  headerChrome: {
    mode: "actions",
    actions: KG_ADD_DATA_MODEL_HEADER_ACTIONS,
  },
};

export const KG_SIDEBAR_NAV_SECTIONS = KG_SECTIONS.filter(
  (section) => section.showInSidebar,
);

export const KG_SIDEBAR_SECTION_PATH = Object.fromEntries(
  KG_SECTIONS.map((section) => [section.id, section.pathSegment]),
) as Record<KgSidebarSection, string>;

const sectionByPathSegment = Object.fromEntries(
  KG_SECTIONS.map((section) => [section.pathSegment, section]),
) as Record<string, KgSectionConfig>;

const sectionById = Object.fromEntries(
  KG_SECTIONS.map((section) => [section.id, section]),
) as Record<KgSidebarSection, KgSectionConfig>;

export const isKgAddDataModelPath = (pathname: string): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "knowledge-graph" || segments[1] !== "data-models") {
    return false;
  }
  const third = segments[2];
  return third === "new" || Boolean(third);
};

export const resolveKgSectionFromPath = (
  pathname: string,
): KgSectionConfig | undefined => {
  if (isKgAddDataModelPath(pathname)) {
    return KG_ADD_DATA_MODEL_SECTION;
  }
  const segments = pathname.split("/").filter(Boolean);
  const segment = segments[1];
  return segment ? sectionByPathSegment[segment] : sectionById.canvas;
};

export const resolveKgSectionHeaderTitle = (
  section: KgSectionConfig,
  ctx: { viewMode?: ViewMode },
): string => {
  const { headerTitle } = section;
  return typeof headerTitle === "function" ? headerTitle(ctx) : headerTitle;
};

export const getKgSectionConfig = (
  id: KgSidebarSection,
): KgSectionConfig => sectionById[id];
